import { isBenchmark, isRowAverages } from './checks'
import { ItemType, SplitOption, SubjectType, UIDSList } from '@aytm/app/insights/dashboard/models/types.ClientDTO'

export function getUidsSet(items: ItemType[]): UIDSList {
  return (
    items?.reduce((acc, item) => {
      acc.add(item?.uid)
      return acc
      // eslint-disable-next-line immutablejs/no-native-map-set
    }, new window.Set<string>()) || new window.Set<string>()
  )
}

export function getWaveUidsSet(items: ItemType[]): UIDSList {
  return (
    items
      ?.filter(item => !isBenchmark({ item }) && !isRowAverages({ item }))
      .reduce((acc, item) => {
        acc.add(item?.waveUid)
        return acc
        // eslint-disable-next-line immutablejs/no-native-map-set
      }, new window.Set<string>()) || new window.Set<string>()
  )
}

export function getPinnedUidsSet(items: ItemType[]): UIDSList {
  return (
    items?.reduce((acc, item) => {
      if (item.pinned) acc.add(item.uid)
      return acc
      // eslint-disable-next-line immutablejs/no-native-map-set
    }, new window.Set<string>()) || new window.Set<string>()
  )
}

const standardIndexSort = <T extends { index: number }>(a: T, b: T) => a.index - b.index

export function getSplitOptions(subjects: Record<string, SubjectType>, waveUids: UIDSList): SplitOption[] {
  return subjects
    ? Object.values(subjects)
        .filter(subject => !isBenchmark({ subject }) && !isRowAverages({ subject }))
        .reduce(
          (acc, cur) => {
            if (waveUids.has(cur.waveUid) && cur.split && !acc[1].has(cur.split.id)) {
              acc[0].push(cur.split)
              acc[1].add(cur.split.id)
            }
            return acc
          },
          // eslint-disable-next-line immutablejs/no-native-map-set
          [[], new window.Set()] as [
            {
              id: string | number
              index: number
              text: string
            }[],
            Set<string | number>
          ]
        )[0]
        .sort(standardIndexSort)
    : []
}

export function getWaveOptions(subjects: Record<string, SubjectType>, waveUids: UIDSList): SplitOption[] {
  return subjects
    ? Object.values(subjects)
        .filter(subject => !isBenchmark({ subject }) && !isRowAverages({ subject }))
        .reduce(
          (acc, cur) => {
            if (waveUids.has(cur.waveUid) && cur.wave && !acc[1].has(cur.wave.id)) {
              acc[0].push(cur.wave)
              acc[1].add(cur.wave.id)
            }
            return acc
          },
          // eslint-disable-next-line immutablejs/no-native-map-set
          [[], new window.Set()] as [
            {
              id: string | number
              index: number
              text: string
            }[],
            Set<string | number>
          ]
        )[0]
        .sort(standardIndexSort)
    : []
}

export function combineSplitGroups(subjects: Record<string, SubjectType> = {}, items: ItemType[] = []): ItemType[] {
  const waveUids = getWaveUidsSet(items)
  const waveOptions = getWaveOptions(subjects, waveUids)
  const splitOptions = getSplitOptions(subjects, waveUids)
  return waveOptions.reduce((acc, waveOption) => {
    for (const splitOption of splitOptions) {
      const subject = subjects
        ? Object.values(subjects).find(v => v.wave?.id === waveOption.id && v.split?.id === splitOption.id)
        : null
      const item = subject ? items.find(item => item.uid === subject.uid && item.waveUid === subject.waveUid) : null
      if (item) {
        acc.push(item)
      } else {
        acc.push({
          uid: subject?.uid || `empty-${waveOption.id}-${splitOption.id}`,
          waveUid: subject?.waveUid || `empty-${waveOption.id}`,
          pinned: false,
          letter: null,
          new: false
        })
      }
    }
    return acc
  }, [] as ItemType[])
}

export const cleanItems = (items: ItemType[] = [], data: Record<ItemType['uid'], unknown> = {}) =>
  items.filter(item => Boolean(data[item.uid]))
